import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Award, Beaker, Users, ArrowRight, Zap, Heart, Brain, Dna, FlaskConical, Microscope, Target, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const AboutPage = () => {
  return (
    <div className="bg-[#050505]" data-testid="about-page">

      {/* Hero Section */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold uppercase tracking-[0.2em] text-[#00F0FF] border border-[#00F0FF]/30 rounded-full bg-[#00F0FF]/5">
              About RENEW LABS
            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="about-hero-title"
            >
              The Future of<br />
              <span className="text-gradient-cyan">Peptide Science</span>
            </h1>
            <p className="text-lg text-[#A1A7B5] max-w-2xl mx-auto">
              At RENEW LABS, we stand at the intersection of cutting-edge biotechnology and precision research.
              Our mission is to provide the highest purity peptides that empower groundbreaking scientific discovery.
            </p>
          </div>
        </div>
      </section>

      {/* 4-Panel Feature Grid — inspired by reference images */}
      <section className="py-16 relative z-[1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Panel 1: Unlock Your Potential */}
            <div className="relative group overflow-hidden rounded-2xl border border-[#1E2532] bg-gradient-to-br from-[#0a0f1a] to-[#0C0E14]" data-testid="panel-unlock">
              <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/35967917/pexels-photo-35967917.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')] bg-cover bg-center opacity-30 group-hover:opacity-40 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
              <div className="relative z-10 p-8 sm:p-10 min-h-[320px] flex flex-col justify-end">
                <div className="w-14 h-14 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center mb-5">
                  <Dna className="w-7 h-7 text-[#00F0FF]" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Unlocking Potential.
                </h3>
                <p className="text-[#A1A7B5] text-sm leading-relaxed">
                  Peptides are short chains of amino acids — the fundamental building blocks of life. They serve as signaling molecules that instruct cells to perform specific functions, from tissue repair to hormone regulation. By harnessing these natural messengers, researchers are unlocking pathways that were once thought impossible.
                </p>
              </div>
            </div>

            {/* Panel 2: Precision Peptide Science */}
            <div className="relative group overflow-hidden rounded-2xl border border-[#1E2532] bg-gradient-to-br from-[#0a0f1a] to-[#0C0E14]" data-testid="panel-precision">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1773429492659-a3192673990f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxETkElMjBoZWxpeCUyMGdsb3dpbmclMjBibHVlJTIwc2NpZW5jZSUyMGxhYm9yYXRvcnl8ZW58MHx8fHwxNzc0OTk2OTA4fDA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-30 group-hover:opacity-40 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
              <div className="relative z-10 p-8 sm:p-10 min-h-[320px] flex flex-col justify-end">
                <div className="w-14 h-14 rounded-xl bg-purple-500/10 flex items-center justify-center mb-5">
                  <FlaskConical className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Precision Peptide Science.
                </h3>
                <p className="text-[#A1A7B5] text-sm leading-relaxed">
                  Modern solid-phase peptide synthesis (SPPS) allows scientists to engineer peptide sequences with extraordinary precision. Each amino acid is added one at a time, enabling researchers to design molecules that target specific receptors, enzymes, and biological pathways with pinpoint accuracy.
                </p>
              </div>
            </div>

            {/* Panel 3: Targeted Cellular Repair */}
            <div className="relative group overflow-hidden rounded-2xl border border-[#1E2532] bg-gradient-to-br from-[#0a0f1a] to-[#0C0E14]" data-testid="panel-repair">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576086213369-97a306d36557?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxiaW9sb2dpY2FsJTIwY2VsbCUyMG1pY3Jvc2NvcHklMjBuZW9uJTIwZ2xvd3xlbnwwfHx8fDE3NzQ5OTY5MTB8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-30 group-hover:opacity-40 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
              <div className="relative z-10 p-8 sm:p-10 min-h-[320px] flex flex-col justify-end">
                <div className="w-14 h-14 rounded-xl bg-pink-500/10 flex items-center justify-center mb-5">
                  <Target className="w-7 h-7 text-pink-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Targeted Cellular Repair.
                </h3>
                <p className="text-[#A1A7B5] text-sm leading-relaxed">
                  Peptides like BPC-157 and TB-500 have shown remarkable ability to accelerate tissue repair at the cellular level. They work by promoting angiogenesis, reducing inflammation, and signaling growth factors — enabling the body's own repair mechanisms to function at peak efficiency.
                </p>
              </div>
            </div>

            {/* Panel 4: Revitalize From Within */}
            <div className="relative group overflow-hidden rounded-2xl border border-[#1E2532] bg-gradient-to-br from-[#0a0f1a] to-[#0C0E14]" data-testid="panel-revitalize">
              <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1576086686350-2f5dba3ffeb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxiaW9sb2dpY2FsJTIwY2VsbCUyMG1pY3Jvc2NvcHklMjBuZW9uJTIwZ2xvd3xlbnwwfHx8fDE3NzQ5OTY5MTB8MA&ixlib=rb-4.1.0&q=85')] bg-cover bg-center opacity-30 group-hover:opacity-40 transition-opacity duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/50 to-transparent" />
              <div className="relative z-10 p-8 sm:p-10 min-h-[320px] flex flex-col justify-end">
                <div className="w-14 h-14 rounded-xl bg-green-500/10 flex items-center justify-center mb-5">
                  <Sparkles className="w-7 h-7 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Revitalize From Within.
                </h3>
                <p className="text-[#A1A7B5] text-sm leading-relaxed">
                  Anti-aging peptides such as Epithalon and GHK-Cu work at the deepest levels — lengthening telomeres, boosting collagen synthesis, and activating dormant stem cells. Science has shown these peptides can help restore cellular function, turning back the biological clock from within.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What Are Peptides — Deep Dive */}
      <section className="py-20 bg-[#0C0E14]/80 backdrop-blur-sm relative z-[1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#00F0FF] text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
                The Science
              </span>
              <h2
                className="text-3xl sm:text-4xl font-bold text-white mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="what-are-peptides-title"
              >
                What Are Peptides &<br />Why Do They Matter?
              </h2>
              <div className="space-y-5 text-[#A1A7B5] leading-relaxed">
                <p>
                  Peptides are naturally occurring biological molecules made up of 2 to 50 amino acids linked by peptide bonds. Unlike larger proteins, their smaller size allows them to penetrate tissues more efficiently and bind to cell-surface receptors with high specificity.
                </p>
                <p>
                  Your body already produces thousands of peptides — they regulate everything from immune response and metabolic function to sleep cycles and pain perception. However, as we age or face illness, peptide production declines, leading to slower recovery, weakened immunity, and accelerated aging.
                </p>
                <p>
                  This is where synthetic research peptides come in. By replicating or enhancing the body's natural signaling molecules, scientists can study and potentially restore biological processes that have degraded over time.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="glass-card p-6 flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0">
                  <Zap className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">High Bioavailability</h4>
                  <p className="text-[#A1A7B5] text-sm">Peptides are small enough to pass through cellular membranes, reaching targets that larger molecules cannot access.</p>
                </div>
              </div>
              <div className="glass-card p-6 flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Receptor-Specific Binding</h4>
                  <p className="text-[#A1A7B5] text-sm">Each peptide is designed to interact with specific receptors, minimizing off-target effects and maximizing precision.</p>
                </div>
              </div>
              <div className="glass-card p-6 flex items-start space-x-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Natural & Well-Tolerated</h4>
                  <p className="text-[#A1A7B5] text-sm">Because peptides mirror the body's own signaling molecules, they are generally well-tolerated in research settings.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How Science Has Amplified Peptides */}
      <section className="py-20 relative z-[1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#00F0FF] text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
              Breakthroughs
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-white mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="science-amplified-title"
            >
              How Science Has Amplified<br />Peptide Research
            </h2>
            <p className="text-[#A1A7B5]">
              In the last two decades, advances in synthesis technology, computational modeling, and genomic research have transformed peptides from curiosity molecules into powerful tools for scientific exploration.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#00F0FF]/20 to-[#00F0FF]/5 flex items-center justify-center border border-[#00F0FF]/20">
                <Microscope className="w-9 h-9 text-[#00F0FF]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Advanced Synthesis
              </h3>
              <p className="text-[#A1A7B5] text-sm leading-relaxed">
                Modern SPPS (Solid-Phase Peptide Synthesis) and FMOC chemistry have made it possible to create peptides with 99%+ purity. Automated synthesizers can produce complex sequences in hours rather than weeks, dramatically accelerating the pace of discovery.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500/20 to-purple-500/5 flex items-center justify-center border border-purple-500/20">
                <Brain className="w-9 h-9 text-purple-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                AI-Driven Discovery
              </h3>
              <p className="text-[#A1A7B5] text-sm leading-relaxed">
                Machine learning algorithms now predict peptide-receptor interactions before synthesis even begins. Computational models simulate folding patterns and binding affinities, enabling researchers to design optimized sequences with unprecedented speed and accuracy.
              </p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-pink-500/20 to-pink-500/5 flex items-center justify-center border border-pink-500/20">
                <Beaker className="w-9 h-9 text-pink-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Enhanced Stability
              </h3>
              <p className="text-[#A1A7B5] text-sm leading-relaxed">
                Peptide stapling, cyclization, and PEGylation techniques have solved the historic problem of peptide degradation. These modifications dramatically increase half-life and stability, making peptides viable tools for long-term research studies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits of Peptide Research — Timeline Style */}
      <section className="py-20 bg-[#0C0E14]/80 backdrop-blur-sm relative z-[1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-[#00F0FF] text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
              Research Applications
            </span>
            <h2
              className="text-3xl sm:text-4xl font-bold text-white mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="benefits-title"
            >
              The Benefits of<br />Peptide Research
            </h2>
            <p className="text-[#A1A7B5]">
              From wound healing to neuroprotection, peptides are being studied across virtually every field of biomedical research. Here are some of the most promising areas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: <Heart className="w-6 h-6" />,
                color: 'text-red-400 bg-red-500/10',
                title: 'Tissue Repair & Recovery',
                desc: 'BPC-157 and TB-500 are among the most studied peptides for tissue regeneration. Research has demonstrated accelerated healing of tendons, ligaments, muscles, and even gut lining through enhanced growth factor signaling and angiogenesis.'
              },
              {
                icon: <Brain className="w-6 h-6" />,
                color: 'text-purple-400 bg-purple-500/10',
                title: 'Cognitive Function & Neuroprotection',
                desc: 'Peptides like Selank and Semax have shown neuroprotective properties in research settings. Studies indicate enhanced BDNF (Brain-Derived Neurotrophic Factor) expression, which plays a critical role in memory, learning, and neural plasticity.'
              },
              {
                icon: <Sparkles className="w-6 h-6" />,
                color: 'text-yellow-400 bg-yellow-500/10',
                title: 'Anti-Aging & Longevity',
                desc: 'Epithalon activates telomerase — the enzyme responsible for lengthening telomeres, the protective caps on chromosomes. GHK-Cu promotes collagen synthesis and has been shown to reset gene expression patterns to a more youthful state in over 4,000 genes.'
              },
              {
                icon: <Shield className="w-6 h-6" />,
                color: 'text-green-400 bg-green-500/10',
                title: 'Immune System Modulation',
                desc: 'Thymosin Alpha-1 and LL-37 have demonstrated powerful immunomodulatory effects. Research shows they enhance T-cell maturation, boost innate immunity, and help regulate overactive immune responses — critical in autoimmune and infectious disease research.'
              },
              {
                icon: <Zap className="w-6 h-6" />,
                color: 'text-[#00F0FF] bg-[#00F0FF]/10',
                title: 'Metabolic Optimization',
                desc: 'Semaglutide and Retatrutide are at the forefront of metabolic peptide research. These GLP-1 receptor agonists have shown remarkable results in weight management studies, improving insulin sensitivity, reducing appetite, and promoting fat oxidation.'
              },
              {
                icon: <Dna className="w-6 h-6" />,
                color: 'text-pink-400 bg-pink-500/10',
                title: 'Growth Hormone Secretion',
                desc: 'CJC-1295, Ipamorelin, and Tesamorelin stimulate natural growth hormone release through the GHRH pathway. Unlike synthetic HGH, these peptides work with the body\'s own pulsatile release pattern, promoting muscle preservation, fat reduction, and improved sleep quality in research models.'
              },
            ].map((item, i) => (
              <div key={i} className="glass-card p-6 flex items-start space-x-4 hover:border-[#1E2532]/80 transition-colors">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-2">{item.title}</h4>
                  <p className="text-[#A1A7B5] text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment / Quality */}
      <section className="py-20 relative z-[1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '99%+', label: 'Purity Guaranteed', sub: 'HPLC verified' },
                  { value: '31', label: 'Research Peptides', sub: 'curated catalog' },
                  { value: 'USA', label: 'Based & Shipped', sub: 'domestic supply' },
                  { value: '24-48hr', label: 'Processing Time', sub: '24-48 hours' },
                ].map((stat, i) => (
                  <div key={i} className="glass-card p-6 text-center">
                    <p className="text-3xl font-bold text-[#00F0FF] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stat.value}
                    </p>
                    <p className="text-white text-sm font-medium">{stat.label}</p>
                    <p className="text-[#A1A7B5] text-xs mt-1">{stat.sub}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="text-[#00F0FF] text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">
                Our Commitment
              </span>
              <h2
                className="text-3xl sm:text-4xl font-bold text-white mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="commitment-title"
              >
                Why Researchers<br />Trust RENEW LABS
              </h2>
              <div className="space-y-5 text-[#A1A7B5] leading-relaxed">
                <p>
                  Every peptide in our catalog undergoes rigorous third-party HPLC and mass spectrometry testing to verify identity and purity, ensuring complete transparency.
                </p>
                <p>
                  Our peptides are synthesized using pharmaceutical-grade amino acids and stored in climate-controlled environments to maintain stability. From synthesis to your lab, every step follows strict quality control protocols.
                </p>
                <p>
                  We believe that great science starts with great materials. That's why we never cut corners — because your research deserves nothing less than the best.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-[#0C0E14]/80 backdrop-blur-sm relative z-[1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2
              className="text-3xl sm:text-4xl font-bold text-white mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Our Core Values
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { icon: <Shield className="w-7 h-7 text-[#00F0FF]" />, title: 'Purity First', desc: 'Every product tested to 99%+ purity via independent HPLC analysis.' },
              { icon: <Award className="w-7 h-7 text-purple-400" />, title: 'Science-Driven', desc: 'Our catalog is curated based on peer-reviewed research and clinical data.' },
              { icon: <Users className="w-7 h-7 text-pink-400" />, title: 'Researcher-Focused', desc: 'Built by scientists, for scientists. We understand what your lab needs.' },
              { icon: <Beaker className="w-7 h-7 text-green-400" />, title: 'Innovation', desc: 'Continuously expanding our catalog with the latest peptide discoveries.' },
            ].map((value, i) => (
              <div key={i} className="glass-card p-6 text-center hover:border-[#00F0FF]/20 transition-colors">
                <div className="w-14 h-14 mx-auto mb-5 rounded-xl bg-white/5 flex items-center justify-center">
                  {value.icon}
                </div>
                <h3 className="text-white font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="text-[#A1A7B5] text-sm">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative z-[1]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl sm:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Advance Your Research?
          </h2>
          <p className="text-lg text-[#A1A7B5] mb-8 max-w-2xl mx-auto">
            Explore our complete catalog of research-grade peptides. Every product is backed by our purity guarantee.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button className="btn-primary flex items-center space-x-2 group" data-testid="cta-explore-btn">
                <span>Explore Our Catalog</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;
